import type { MetricCanonicalName, SetPlanInfoId, StepPlanInfoId } from '@az3fr/core';
import { useCallback } from 'react';

import type { SetActions } from '@/features/workout-creator/ui/v2/sets-editor/types/sets-editor.types';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { useAppDispatch } from '@/hooks/store.hook';

/**
 * Custom hook that provides all action dispatchers for the SetsEditor
 * Consolidates all dispatch calls with proper memoization
 */
export function useSetActions(): SetActions {
	const dispatch = useAppDispatch();

	const updateSetMetric = useCallback(
		(setPlanId: SetPlanInfoId, metricCanonicalName: MetricCanonicalName, value: number | null) => {
			dispatch(
				workoutCreatorThunks.updateSetPlanTargetMetric({
					setPlanId,
					metricCanonicalName,
					value,
				}),
			);
		},
		[dispatch],
	);

	const updateTemplateSet = useCallback(
		(stepPlanId: StepPlanInfoId, templateSets: number) => {
			dispatch(
				workoutCreatorThunks.updateStepPlanProperties({
					stepPlanId,
					changes: { templateSets },
				}),
			);
		},
		[dispatch],
	);

	const deleteSet = useCallback(
		(setPlanId: SetPlanInfoId) => {
			dispatch(workoutCreatorThunks.removeSetPlan({ setPlanId }));
		},
		[dispatch],
	);

	const addSet = useCallback(
		(stepPlanId: StepPlanInfoId) => {
			dispatch(workoutCreatorThunks.addSetPlan({ stepPlanId }));
		},
		[dispatch],
	);

	return {
		updateSetMetric,
		updateTemplateSet,
		deleteSet,
		addSet,
	};
}
